import React, { useEffect, useState } from 'react';
import { useDocumentDownload } from '../../common/api/hooks/use-document-download';
import { Badge } from '../../common/components/Badge';
import { Button } from '../../common/components/Button';
import { hasRole, useAuth } from '../../common/auth/AuthProvider';
import { formatDueDate, formatMoney, formatPercentage } from '../../common/lib/format';
import { escapePrintHtml, getPrintBrandMarkup, getPrintBrandStyles, printDocument } from '../../common/lib/print';
import { PageHeader } from '../../common/layout/PageHeader';
import { clientStatusOptions, initialClientForm, initialCreditDocumentChecklist } from './data';
import { useClientCredits, useClients } from './hooks';
import { CreditReversal, ReversalReceipt } from './components/credit-reversal';
import {
  getClientRiskColor,
  getClientRiskLabel,
  getPendingCreditDocumentLabels,
  isCreditDocumentChecklistComplete,
  normalizeDateInput,
  displayBirthDate,
  toClientFormState,
} from './lib';
import { printApprovedPaymentSchedule, printCreditContract, printDisbursementRequest } from '../solicitudes/lib';
import type { CreditContractData } from '../solicitudes/types';
import type { Client, ClientCredit, ClientFilters, ClientFormState, CreditDocumentChecklist, CreditDocumentType, PaymentVoucher } from './types';

export const ClientesView: React.FC = () => {
  const { downloadDocument, downloadError, downloadingId } = useDocumentDownload();
  const { user } = useAuth();
  const canAssignCreditAdvisor = hasRole(user, ['ADMIN']);
  const canUseCashSessions = hasRole(user, ['ADMIN', 'CASHIER']);
  const { clients, createClient, error, isCreating, isLoading, isUpdating, isRefreshing, refetch, updateClient,
    page, setPage, total, filters, setFilters } = useClients();
  const {
    reverseCredit, isReversing, reversalVoucher,
    advisors,
    assignAdvisor,
    credits,
    disbursement,
    disburseCredit,
    error: creditsError,
    fetchCredits,
    isDisbursing,
    isAssigningAdvisor,
    isLoading: isLoadingCredits,
    isPaying,
    openCashSessions,
    payInstallments,
    prepareDocuments,
    confirmDocument,
    voucher: paidVoucherPreview,
  } = useClientCredits(canAssignCreditAdvisor, canUseCashSessions);
  const voucher = paidVoucherPreview;
  const [form, setForm] = useState<ClientFormState>(initialClientForm);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedCreditId, setSelectedCreditId] = useState<string | null>(null);
  const [reversalCreditId, setReversalCreditId] = useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [documentError, setDocumentError] = useState<string | null>(null);
  const isEditMode = Boolean(selectedClient);

  useEffect(() => {
    if (!credits?.length) {
      setSelectedCreditId(null);
      return;
    }

    setSelectedCreditId((currentId) => {
      if (currentId && credits.some((credit) => credit.id === currentId)) return currentId;
      return credits[0].id;
    });
  }, [credits]);

  const selectedCredit = credits?.find((credit) => credit.id === selectedCreditId) ?? null;
  const reversalCredit = credits?.find((credit) => credit.id === reversalCreditId) ?? null;
  const parsedPaymentAmount = Number(paymentAmount);
  const isPaymentAmountValid = Number.isFinite(parsedPaymentAmount) && parsedPaymentAmount > 0;
  const canPayInstallments = canUseCashSessions;
  const canDisburseCredits = canUseCashSessions;
  const canPrintClientCreditDocuments = canUseCashSessions;
  const ownOpenCashSession = user ? openCashSessions?.find((session) => session.userId === user.id) ?? null : null;
  const isSelectedCreditDisbursed = selectedCredit?.status === 'ACTIVO' || selectedCredit?.status === 'VENCIDO';
  const selectedCreditDocuments = selectedCredit?.generatedDocuments ?? initialCreditDocumentChecklist;
  const areSelectedCreditDocumentsReady = isCreditDocumentChecklistComplete(selectedCreditDocuments);
  const pendingCreditDocumentLabels = getPendingCreditDocumentLabels(selectedCreditDocuments);

  const handleChange = (field: keyof ClientFormState, value: string | boolean) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: typeof value === 'string' && field === 'specialInterestRate' ? limitDecimals(value, 3) : value,
    }));
  };

  const handleFilterChange = (field: keyof ClientFilters, value: string) => {
    setPage(1);
    setFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value,
    }));
  };

  const handleCreateClick = () => {
    setSelectedClient(null);
    setSelectedCreditId(null);
    setSelectedScheduleId(null);
    setPaymentAmount('');
    setForm(initialClientForm);
    setIsFormOpen(true);
  };

  const handleEditClick = () => {
    if (!selectedClient) return;

    setForm(toClientFormState(selectedClient));
    setIsFormOpen(true);
  };

  const handleSelectClient = (client: Client) => {
    setReversalCreditId(null);
    setSelectedClient(client);
    setSelectedCreditId(null);
    setSelectedScheduleId(null);
    setPaymentAmount('');
    void fetchCredits(client.id);
  };

  const handlePayInstallments = async () => {
    if (!canPayInstallments) return;
    if (!selectedCredit) return;
    if (!user) return;
    if (!isPaymentAmountValid) return;
    if (!isSelectedCreditDisbursed) return;

    const printWindow = window.open('', '_blank', 'width=960,height=680');
    const paidVoucher = await payInstallments(selectedCredit.id, parsedPaymentAmount, user.id);

    if (!paidVoucher) {
      printWindow?.close();
      return;
    }
    setSelectedScheduleId(null);
    setPaymentAmount('');
    printVoucher(paidVoucher, printWindow);
  };

  const handleDisburseCredit = async (creditId: string) => {
    if (!canDisburseCredits) return;
    if (!user) return;
    const credit = credits?.find((item) => item.id === creditId);
    if (!credit || !isCreditDocumentChecklistComplete(credit.generatedDocuments)) return;

    await disburseCredit(creditId, user.id);
  };

  const handlePrintApprovedDocument = async (documentType: CreditDocumentType, printDocumentHandler: (printWindow: Window, contract: CreditContractData) => void) => {
    if (!selectedClient) return;
    if (!selectedCredit) return;
    if (selectedCredit.status === 'CANCELADO') return;

    const printWindow = window.open('', '_blank', 'width=960,height=760');

    setDocumentError(null);
    if (!printWindow) { setDocumentError('Permita ventanas emergentes para generar documentos'); return; }
    try {
      const prepared = selectedCredit.status === 'APROBADO' ? await prepareDocuments(selectedCredit.id) : selectedCredit;
      if (!prepared?.documentDate) { printWindow.close(); return; }
      printDocumentHandler(printWindow, toCreditContractData(selectedClient, prepared));
      if (selectedCredit.status === 'APROBADO') await confirmDocument(prepared.id, documentType, prepared.documentDate);
    } catch {
      printWindow.close();
      setDocumentError('No se pudo generar el documento');
    }
  };

  const getSubmitLabel = () => {
    if (isUpdating) return 'Actualizando...';
    if (isCreating) return 'Guardando...';
    if (isEditMode) return 'Actualizar cliente';
    return 'Guardar cliente';
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedClient) {
      const updated = await updateClient(selectedClient.id, form);

      if (!updated) return;

      setSelectedClient(null);
      setSelectedCreditId(null);
      setSelectedScheduleId(null);
      setPaymentAmount('');
      setForm(initialClientForm);
      setIsFormOpen(false);
      return;
    }

    const created = await createClient(form);

    if (!created) return;

    setForm(initialClientForm);
    setIsFormOpen(false);
  };

  if (isLoading) {
    return (
      <>
        <PageHeader
          actions={<Button variant="outline">Cargando</Button>}
          description="Consulta clientes, deuda vigente, estado de riesgo y datos de contacto."
          title="Clientes"
        />
        <div className="card">
          <div className="card__body">Cargando clientes...</div>
        </div>
      </>
    );
  }

  if (error && !clients) {
    return (
      <>
        <PageHeader
          actions={<Button onClick={() => void refetch()}>Reintentar</Button>}
          description="Consulta clientes, deuda vigente, estado de riesgo y datos de contacto."
          title="Clientes"
        />
        <div className="card">
          <div className="card__body message--error">{error}</div>
        </div>
      </>
    );
  }

  if (!clients) {
    return (
      <>
        <PageHeader
          actions={<Button onClick={handleCreateClick}>Nuevo cliente</Button>}
          description="Consulta clientes, deuda vigente, estado de riesgo y datos de contacto."
          title="Clientes"
        />
        <div className="card">
          <div className="card__body">No hay clientes registrados.</div>
        </div>
      </>
    );
  }

  const filteredClients = clients;

  return (
    <>
      <PageHeader
        actions={
          <>
            <Button onClick={isFormOpen ? () => setIsFormOpen(false) : handleCreateClick} variant="outline">
              {isFormOpen ? 'Cerrar formulario' : 'Nuevo cliente'}
            </Button>
            <Button disabled={!selectedClient} onClick={handleEditClick} variant="secondary">
              Actualizar
            </Button>
            <Button onClick={() => void refetch({ silent: true })} variant="ghost">
              Recargar
            </Button>
          </>
        }
        description="Consulta clientes, deuda vigente, estado de riesgo y datos de contacto."
        title="Clientes"
      />
      {error ? (
        <div className="card">
          <div className="card__body message--error">{error}</div>
        </div>
      ) : null}
      {isFormOpen ? (
        <div className="card">
          <div className="card__header">
            <div>
              <h2 className="card__title">{isEditMode ? 'Actualizar cliente' : 'Registrar cliente'}</h2>
              <p className="card__description">
                {isEditMode ? 'Edita el cliente seleccionado y guarda los cambios.' : 'Los datos se guardan en PostgreSQL mediante el backend.'}
              </p>
            </div>
          </div>
          <div className="card__body">
            <form className="form-grid" onSubmit={handleSubmit}>
              <div className="field">
                <label htmlFor="firstName">Nombre</label>
                <input id="firstName" onChange={(event) => handleChange('firstName', event.target.value)} required value={form.firstName} />
              </div>
              <div className="field">
                <label htmlFor="lastName">Apellido</label>
                <input id="lastName" onChange={(event) => handleChange('lastName', event.target.value)} required value={form.lastName} />
              </div>
              <div className="field">
                <label htmlFor="dni">DNI</label>
                <input id="dni" maxLength={8} onChange={(event) => handleChange('dni', event.target.value)} required value={form.dni} />
              </div>
              <div className="field">
                <label htmlFor="phone">Telefono</label>
                <input id="phone" onChange={(event) => handleChange('phone', event.target.value)} required value={form.phone} />
              </div>
              <div className="field">
                <label htmlFor="birthDate">Fecha de nacimiento</label>
                <input
                  id="birthDate"
                  inputMode="numeric"
                  maxLength={10}
                  onChange={(event) => handleChange('birthDate', normalizeDateInput(event.target.value))}
                  pattern="\d{2}-\d{2}-\d{4}"
                  placeholder="DD-MM-YYYY"
                  type="text"
                  value={form.birthDate}
                />
              </div>
              <div className="field">
                <label htmlFor="email">Correo</label>
                <input id="email" onChange={(event) => handleChange('email', event.target.value)} type="email" value={form.email} />
              </div>
              <div className="field">
                <label htmlFor="personalAddress">Direccion personal</label>
                <input id="personalAddress" onChange={(event) => handleChange('personalAddress', event.target.value)} value={form.personalAddress} />
              </div>
              <div className="field">
                <label htmlFor="personalAddressReference">Referencia de la direccion del cliente</label>
                <input id="personalAddressReference" onChange={(event) => handleChange('personalAddressReference', event.target.value)} value={form.personalAddressReference} />
              </div>
              <div className="field">
                <label htmlFor="department">Departamento</label>
                <input id="department" onChange={(event) => handleChange('department', event.target.value)} value={form.department} />
              </div>
              <div className="field">
                <label htmlFor="province">Provincia</label>
                <input id="province" onChange={(event) => handleChange('province', event.target.value)} value={form.province} />
              </div>
              <div className="field">
                <label htmlFor="district">Distrito</label>
                <input id="district" onChange={(event) => handleChange('district', event.target.value)} value={form.district} />
              </div>
              <div className="field">
                <label htmlFor="referenceName">Nombre de contacto</label>
                <input id="referenceName" onChange={(event) => handleChange('referenceName', event.target.value)} value={form.referenceName} />
              </div>
              <div className="field">
                <label htmlFor="referencePhone">Telefono de contacto</label>
                <input id="referencePhone" onChange={(event) => handleChange('referencePhone', event.target.value)} value={form.referencePhone} />
              </div>
              <fieldset className="form-grid" style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                <legend>Conyugue (opcional)</legend>
                <label className="field field--checkbox" htmlFor="hasSpouse">
                  <input
                    checked={form.hasSpouse}
                    id="hasSpouse"
                    onChange={(event) => handleChange('hasSpouse', event.target.checked)}
                    type="checkbox"
                  />
                  Agregar conyugue
                </label>
                {form.hasSpouse ? (
                  <>
                    <div className="field">
                      <label htmlFor="spouseFullName">Nombre completo del conyugue</label>
                      <input id="spouseFullName" onChange={(event) => handleChange('spouseFullName', event.target.value)} value={form.spouseFullName} />
                    </div>
                    <div className="field">
                      <label htmlFor="spousePhone">Telefono del conyugue</label>
                      <input id="spousePhone" onChange={(event) => handleChange('spousePhone', event.target.value)} value={form.spousePhone} />
                    </div>
                  </>
                ) : null}
              </fieldset>
              <fieldset className="form-grid" style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                <legend>Aval (opcional)</legend>
                <label className="field field--checkbox" htmlFor="hasGuarantor">
                  <input
                    checked={form.hasGuarantor}
                    id="hasGuarantor"
                    onChange={(event) => handleChange('hasGuarantor', event.target.checked)}
                    type="checkbox"
                  />
                  Agregar aval
                </label>
                {form.hasGuarantor ? (
                  <>
                    <div className="field">
                      <label htmlFor="guarantorFullName">Nombre completo del aval</label>
                      <input id="guarantorFullName" onChange={(event) => handleChange('guarantorFullName', event.target.value)} value={form.guarantorFullName} />
                    </div>
                    <div className="field">
                      <label htmlFor="guarantorPhone">Telefono del aval</label>
                      <input id="guarantorPhone" onChange={(event) => handleChange('guarantorPhone', event.target.value)} value={form.guarantorPhone} />
                    </div>
                  </>
                ) : null}
              </fieldset>
              <fieldset className="form-grid" style={{ gridColumn: '1 / -1', minWidth: 0 }}>
              <legend>Datos de negocio (opcional)</legend>
              <div className="field">
                <label htmlFor="businessRuc">RUC</label>
                <input id="businessRuc" maxLength={11} pattern="[0-9]{11}" onChange={(event) => handleChange('businessRuc', event.target.value)} value={form.businessRuc} />
              </div>
              <div className="field">
                <label htmlFor="businessName">Nombre del negocio</label>
                <input id="businessName" onChange={(event) => handleChange('businessName', event.target.value)} value={form.businessName} />
              </div>
              <div className="field">
                <label htmlFor="businessPhone">Telefono del negocio</label>
                <input id="businessPhone" onChange={(event) => handleChange('businessPhone', event.target.value)} value={form.businessPhone} />
              </div>
              <div className="field">
                <label htmlFor="businessActivity">Giro del negocio</label>
                <input id="businessActivity" onChange={(event) => handleChange('businessActivity', event.target.value)} value={form.businessActivity} />
              </div>
              <div className="field">
                <label htmlFor="businessAddress">Direccion del negocio</label>
                <input id="businessAddress" onChange={(event) => handleChange('businessAddress', event.target.value)} value={form.businessAddress} />
              </div>
              <div className="field">
                <label htmlFor="businessAddressReference">Referencia de la direccion del negocio</label>
                <input id="businessAddressReference" onChange={(event) => handleChange('businessAddressReference', event.target.value)} value={form.businessAddressReference} />
              </div>
              </fieldset>
              <div className="field">
                <label htmlFor="status">Estado</label>
                <select id="status" onChange={(event) => handleChange('status', event.target.value)} value={form.status}>
                  {clientStatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <label className="field field--checkbox" htmlFor="isSpecial">
                <input
                  checked={form.isSpecial}
                  id="isSpecial"
                  onChange={(event) => handleChange('isSpecial', event.target.checked)}
                  type="checkbox"
                />
                Cliente exclusivo
              </label>
              <div className="actions">
                <Button disabled={isCreating || isUpdating} type="submit">
                  {getSubmitLabel()}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
      <div className="card">
        <div className="card__body">
          <div className="form-grid">
            <div className="field">
              <label htmlFor="filterName">Filtrar por nombre</label>
              <input id="filterName" onChange={(event) => handleFilterChange('name', event.target.value)} placeholder="Nombre o apellido" value={filters.name} />
            </div>
            <div className="field">
              <label htmlFor="filterDni">Filtrar por DNI</label>
              <input id="filterDni" maxLength={8} onChange={(event) => handleFilterChange('dni', event.target.value)} placeholder="DNI" value={filters.dni} />
            </div>
          </div>
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>DNI</th>
              <th>Telefono</th>
              <th>Nacimiento</th>
              <th>Direccion personal</th>
              <th>Direccion negocio</th>
              <th className="table__number">Deuda</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map((client) => (
              <tr data-interactive="true" className={selectedClient?.id === client.id ? 'table__row--selected' : undefined} key={client.id} onClick={() => handleSelectClient(client)}>
                <td>{client.fullName}</td>
                <td>{client.dni}</td>
                <td>{client.phone}</td>
                <td>{displayBirthDate(client.birthDate) || '-'}</td>
                <td>{[client.personalAddress, client.department, client.province, client.district].filter(Boolean).join(', ') || '-'}</td>
                <td>{client.businessAddress ?? '-'}</td>
                <td className="money">{formatMoney(client.totalDebt)}</td>
                <td>
                  <Badge color={getClientRiskColor(client)}>
                    {getClientRiskLabel(client)}
                  </Badge>
                  {client.isSpecial ? <> <Badge color="yellow">Exclusivo</Badge></> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!filteredClients.length ? (
        <div className="card">
          <div className="card__body">No hay clientes que coincidan con los filtros.</div>
        </div>
      ) : null}
      <div className="actions" aria-label="Paginacion de clientes">
        <Button variant="outline" disabled={page === 1 || isRefreshing} onClick={() => setPage(page - 1)}>Anterior</Button>
        <span>Pagina {page} de {Math.max(1, Math.ceil(total / 25))} ({total} clientes)</span>
        <Button variant="outline" disabled={page * 25 >= total || isRefreshing} onClick={() => setPage(page + 1)}>Siguiente</Button>
      </div>
      {selectedClient ? (
        <section className="grid">
          <div className="card">
            <div className="card__header">
              <div>
                <h2 className="card__title">Creditos de {selectedClient.fullName}</h2>
                <p className="card__description">Selecciona credito para ver cuotas.</p>
              </div>
            </div>
            <div className="card__body">
              {creditsError ? <p className="message--error">{creditsError}</p> : null}
              {reversalVoucher ? <ReversalReceipt voucher={reversalVoucher} /> : null}
              {reversalCredit && canAssignCreditAdvisor ? <CreditReversal key={reversalCredit.id} credit={reversalCredit} sessions={openCashSessions} busy={isReversing} submit={reverseCredit} close={() => setReversalCreditId(null)} /> : null}
              {documentError ? <p className="message--error">{documentError}</p> : null}
              {downloadError ? <p className="message--error">{downloadError}</p> : null}
              {selectedCredit?.files.map((file) => (
                <Button key={file.id} disabled={!file.sizeBytes || downloadingId === file.id} variant="outline" onClick={() => void downloadDocument(file.id, file.fileName)}>{file.fileName}</Button>
              ))}
              {disbursement ? (
                <p>
                  Credito {disbursement.creditCode} desembolsado desde {disbursement.cashBox}: {formatMoney(disbursement.amount)}
                </p>
              ) : null}
              {isLoadingCredits ? <p>Cargando creditos...</p> : null}
              {!isLoadingCredits && !credits?.length ? <p>No hay creditos aprobados.</p> : null}
              {credits?.length ? (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Codigo</th>
                        <th className="table__number">Monto credito</th>
                        <th className="table__number">Interes</th>
                        <th className="table__number">Mora</th>
                        <th className="table__number">Valor neto</th>
                        <th>Estado</th>
                        <th>Asesor</th>
                        <th>Accion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credits.map((credit) => {
                        const creditDocuments = credit.generatedDocuments;
                        const areCreditDocumentsReady = isCreditDocumentChecklistComplete(creditDocuments);

                        return (
                        <tr
                          className={selectedCreditId === credit.id ? 'table__row--selected' : undefined}
                          data-interactive="true"
                          key={credit.id}
                          onClick={() => {
                            setSelectedCreditId(credit.id);
                            setSelectedScheduleId(null);
                            setPaymentAmount('');
                          }}
                        >
                          <td>{credit.code}</td>
                          <td className="money">{formatMoney(credit.principalAmount)}</td>
                          <td className="table__number">{formatPercentage(credit.interestRate)}</td>
                          <td className="money">{formatMoney(credit.overdueAmount)}</td>
                          <td className="money">{formatMoney(credit.netValue)}</td>
                          <td>{credit.status}</td>
                          <td onClick={(event) => event.stopPropagation()}>
                            {canAssignCreditAdvisor && advisors?.length ? (
                              <select
                                aria-label={`Asesor de ${credit.code}`}
                                disabled={isAssigningAdvisor}
                                onChange={(event) => void assignAdvisor(credit.id, event.target.value)}
                                value={credit.advisorId}
                              >
                                {advisors.map((advisor) => (
                                  <option key={advisor.id} value={advisor.id}>
                                    {advisor.fullName}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              credit.advisorName
                            )}
                          </td>
                          <td onClick={(event) => event.stopPropagation()}>
                            {credit.status === 'APROBADO' && canDisburseCredits ? (
                              <Button
                                className="button--compact"
                                disabled={!canDisburseCredits || !ownOpenCashSession || isDisbursing || !areCreditDocumentsReady}
                                onClick={() => void handleDisburseCredit(credit.id)}
                              >
                                Desembolsar
                              </Button>
                            ) : null}
                            {canAssignCreditAdvisor && credit.status !== 'CANCELADO' ? <Button className="button--compact" variant="destructive" disabled={isReversing || isPaying || isDisbursing} onClick={() => setReversalCreditId(credit.id)}>Revertir</Button> : null}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
          <div className="card">
            <div className="card__header">
              <div>
                <h2 className="card__title">Detalle de cuotas</h2>
                <p className="card__description">{selectedCredit ? selectedCredit.code : 'Sin credito seleccionado'}</p>
              </div>
            </div>
            <div className="card__body">
              {selectedCredit && selectedCredit.status === 'APROBADO' && canPrintClientCreditDocuments ? (
                <>
                  <div className="actions">
                    <Button
                      className={selectedCreditDocuments.schedule ? 'button--compact button--document button--document-ready' : 'button--compact button--document'}
                      onClick={() => handlePrintApprovedDocument('schedule', printApprovedPaymentSchedule)}
                      variant="outline"
                    >
                      Cronograma
                    </Button>
                    <Button
                      className={selectedCreditDocuments.contract ? 'button--compact button--document button--document-ready' : 'button--compact button--document'}
                      onClick={() => handlePrintApprovedDocument('contract', printCreditContract)}
                      variant="outline"
                    >
                      Contrato
                    </Button>
                    <Button
                      className={selectedCreditDocuments.disbursementRequest ? 'button--compact button--document button--document-ready' : 'button--compact button--document'}
                      onClick={() => handlePrintApprovedDocument('disbursementRequest', printDisbursementRequest)}
                      variant="outline"
                    >
                      Solicitud de desembolso
                    </Button>
                  </div>
                  {!areSelectedCreditDocumentsReady ? <p className="message--warning">Genera antes de desembolsar: {pendingCreditDocumentLabels}.</p> : null}
                  {areSelectedCreditDocumentsReady ? <p className="message--success">Documentos generados. Credito listo para desembolso.</p> : null}
                </>
              ) : null}
              {voucher ? (
                <div className="voucher">
                  <div>
                    <strong>{voucher.voucherCode}</strong>
                    <span>
                      {voucher.creditCode} · cuotas {voucher.scheduleNumbers.join(', ')} · saldo {formatMoney(voucher.remainingBalance)}
                    </span>
                  </div>
                  <Button className="button--compact" onClick={() => printVoucher(voucher)} variant="outline">
                    Imprimir
                  </Button>
                </div>
              ) : null}
              {selectedCredit && canPayInstallments && !isSelectedCreditDisbursed ? <p className="message--error">Credito debe estar desembolsado para registrar pagos.</p> : null}
              {selectedCredit && canPayInstallments && !ownOpenCashSession ? <p className="message--error">Perfil debe tener una caja abierta para registrar pagos.</p> : null}
              {selectedCredit && canPayInstallments && isSelectedCreditDisbursed && ownOpenCashSession ? (
                <div className="inline-form">
                  <div className="field">
                    <label htmlFor="paymentAmount">Monto a pagar</label>
                    <input
                      id="paymentAmount"
                      min="0.01"
                      onChange={(event) => {
                        setSelectedScheduleId(null);
                        setPaymentAmount(event.target.value);
                      }}
                      placeholder="S/ 0.00"
                      step="0.01"
                      type="number"
                      value={paymentAmount}
                    />
                  </div>
                  <Button disabled={!isPaymentAmountValid || isPaying} onClick={() => void handlePayInstallments()}>
                    {isPaying ? 'Pagando...' : 'Pagar'}
                  </Button>
                </div>
              ) : null}
              {selectedCredit ? (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Cuota</th>
                        <th>Vence</th>
                        <th className="table__number">Capital</th>
                        <th className="table__number">Interes</th>
                        <th className="table__number">Mora</th>
                        <th className="table__number">Total</th>
                        <th>Fecha de pago</th>
                        <th className="table__number">Saldo</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCredit.schedules.map((schedule) => {
                        const pendingAmount = Math.max(0, schedule.totalDue + schedule.penalty - schedule.paidAmount);

                        return (
                          <tr
                            className={selectedScheduleId === schedule.id ? 'table__row--selected' : undefined}
                            data-interactive={canPayInstallments && Boolean(ownOpenCashSession) && isSelectedCreditDisbursed && pendingAmount > 0}
                            key={schedule.id}
                            onClick={() => {
                              if (!canPayInstallments || !ownOpenCashSession || !isSelectedCreditDisbursed || pendingAmount <= 0) return;
                              setSelectedScheduleId(schedule.id);
                              setPaymentAmount(pendingAmount.toFixed(2));
                            }}
                          >
                            <td>{schedule.installmentNo}</td>
                            <td>{formatDueDate(schedule.dueDate)}</td>
                            <td className="money">{formatMoney(schedule.principal)}</td>
                            <td className="money">{formatMoney(schedule.interest)}</td>
                            <td className="money">{formatMoney(schedule.penalty)}</td>
                            <td className="money">{formatMoney(schedule.totalDue)}</td>
                            <td>{schedule.paidAt ? formatDueDate(schedule.paidAt) : '-'}</td>
                            <td className="money">{formatMoney(pendingAmount)}</td>
                            <td>{schedule.status}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>Selecciona un credito.</p>
              )}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};

const toCreditContractData = (client: Client, credit: ClientCredit): CreditContractData => ({
  advisorName: credit.advisorName,
  approvedAt: credit.documentDate ? `${credit.documentDate}T12:00:00-05:00` : credit.approvedAt,
  approvedByName: credit.approvedByName ?? credit.advisorName,
  clientAddress: client.personalAddress,
  clientDni: client.dni,
  clientName: client.fullName,
  creditCode: credit.code,
  installmentAmount: credit.installmentAmount,
  installmentCount: credit.schedules.length,
  interestCalculationMethod: credit.interestCalculationMethod,
  interestRate: credit.interestRate,
  paymentFrequency: credit.paymentFrequency,
  penaltyRate: credit.penaltyRate,
  penaltyTerms: credit.penaltyTerms,
  principalAmount: credit.principalAmount,
  schedules: credit.schedules.map((schedule) => ({
    dueDate: schedule.dueDate,
    installmentNo: schedule.installmentNo,
    interest: schedule.interest,
    principal: schedule.principal,
    totalDue: schedule.totalDue,
  })),
  totalAmount: credit.totalAmount,
});

const limitDecimals = (value: string, decimals: number) => {
  const [integerPart, decimalPart] = value.split('.');

  if (decimalPart === undefined) return value;
  return `${integerPart}.${decimalPart.slice(0, decimals)}`;
};

const printVoucher = (voucher: PaymentVoucher, targetWindow?: Window | null) => {
  const printWindow = targetWindow ?? window.open('', '_blank', 'width=960,height=680');

  if (!printWindow) return;

  const cashierName = escapePrintHtml(voucher.cashierName);
  const clientDni = escapePrintHtml(voucher.clientDni);
  const clientName = escapePrintHtml(voucher.clientName);
  const creditCode = escapePrintHtml(voucher.creditCode);
  const voucherCopy = (includeSignature: boolean) => `
    <section class="voucher-copy">
      ${getPrintBrandMarkup(window.location.origin)}
      <div class="voucher-heading"><h1>Voucher de pago</h1><span>${includeSignature ? 'EMPRESA' : 'CLIENTE'}</span></div>
      <p><span>Cliente</span><strong>${clientName}</strong></p>
      <p><span>DNI / Credito</span><strong>${clientDni} / ${creditCode}</strong></p>
      <p><span>Cuotas</span><strong>${voucher.scheduleNumbers.join(', ')}</strong></p>
      <p class="amount"><span>Total</span><strong>${formatMoney(voucher.amount)}</strong></p>
      <p><span>Saldo pendiente</span><strong>${formatMoney(voucher.remainingBalance)}</strong></p>
      <footer><span>Usuario</span><strong>${cashierName}</strong></footer>
      ${includeSignature ? '<div class="signature"><span>Firma del cliente</span></div>' : ''}
    </section>
  `;

  printWindow.document.write(`
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Voucher de pago</title>
        <style>
          @page { size: auto; margin: 0; }
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; }
          body { color: #111; font-family: Arial, sans-serif; font-size: 9pt; line-height: 1.15; }
          .voucher-sheet { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start; align-content: start; width: 100%; max-width: 210mm; margin: 0; padding: 0 4mm; }
          .voucher-copy { min-width: 0; padding-right: 5mm; break-inside: avoid; }
          .voucher-copy + .voucher-copy { border-left: 1px dashed #777; padding: 0 0 0 5mm; }
          .voucher-heading { display: flex; align-items: center; justify-content: space-between; gap: 3mm; margin: 2mm 0; }
          .voucher-heading span { font-size: 7pt; }
          h1 { font-size: 11pt; margin: 0; }
          p { display: grid; grid-template-columns: 23mm minmax(0, 1fr); gap: 2mm; margin: 1mm 0; }
          p span, footer span { color: #4b5563; }
          p strong, footer strong { overflow-wrap: anywhere; }
          ${getPrintBrandStyles()}
          .print-brand { gap: 2mm; padding-bottom: 2mm; border-bottom-width: 1px; }
          .print-brand__logo { width: 8mm; height: 8mm; }
          .print-brand__name { font-size: 11pt; letter-spacing: 0; }
          .print-brand__tagline { font-size: 7pt; letter-spacing: 0; }
          .amount { border-top: 1px solid #aaa; margin-top: 2mm; padding-top: 2mm; font-size: 11pt; }
          .signature { width: 55mm; max-width: 100%; margin: 7mm auto 0; border-top: 1px solid #111; padding-top: 1mm; text-align: center; font-size: 8pt; }
          footer { display: grid; grid-template-columns: 23mm minmax(0, 1fr); gap: 2mm; margin-top: 1.5mm; border-top: 1px solid #aaa; padding-top: 1.5mm; font-size: 8pt; }
          @media print { .voucher-sheet { padding-top: 0; } }
        </style>
      </head>
      <body>
        <main class="voucher-sheet">
          ${voucherCopy(true)}
          ${voucherCopy(false)}
        </main>
      </body>
    </html>
  `);
  printWindow.document.close();
  printDocument(printWindow);
};
